import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Skeleton from '@mui/material/Skeleton';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { IAttachmentSubSchemaPartial } from '../../../../graphql/type/subSchema/attachment';
import DialogConfirm from '../../_dialog/DialogConfirm';
import StatusIconButton from '../../_iconButton/StatusIconButton';
import DeleteIconButton from '../../_iconButton/DeleteIconButton';
import BaseIconButton from '../../_iconButton/_module/BaseIconButton';

interface IProps {
  onDelete?: (filename: string) => void;
  onPublish?: (filenames: string[]) => void;
  onUnpublish?: (filenames: string[]) => void;
  attachment?: IAttachmentSubSchemaPartial & {
    status: boolean;
    filename: string;
  };
  loading?: boolean;
  defaultLoadingHeight?: string;
}

const useStyles = makeStyles<Theme, IProps>((theme) => ({
  media: {
    height: 0,
    paddingTop: '100%',
    backgroundSize: 'contain',
  },
  action: {
    padding: 0,
    '& button': { borderRadius: 0 },
  },
  card: {
    borderColor: ({ attachment }) =>
      attachment && attachment.status ? theme.palette.primary.main : '#F92949',
    borderWidth: '3px',
    borderStyle: 'solid',
  },
}));

export default function BaseImageUploadItem(props: IProps) {
  const {
    onDelete,
    onPublish,
    onUnpublish,
    attachment,
    loading,
    defaultLoadingHeight,
  } = props;
  const { t } = useTranslation();
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const classes = useStyles(props);

  if (loading || !attachment) {
    return (
      <Skeleton variant="rectangular" height={defaultLoadingHeight || '36px'} />
    );
  }

  const url = attachment.filenameUrl;

  return (
    <>
      {attachment && (
        <>
          <DialogConfirm
            title={t('are_you_sure_you_want_to_delete')}
            open={isOpenDeleteDialog}
            onClose={() => setIsOpenDeleteDialog(false)}
            onConfirm={() => {
              if (onDelete) {
                onDelete(attachment.filename);
              }
            }}
          />
          <ListItem
            button
            onClick={() => {
              window.open(encodeURI(url || ''), '_blank');
            }}
          >
            <ListItemText primary={attachment.filename} />
            <ListItemSecondaryAction>
              <BaseIconButton
                onClick={() => {
                  window.open(encodeURI(url || ''), '_blank');
                }}
                size="small"
              >
                <CloudDownloadIcon color="primary" fontSize="small" />
              </BaseIconButton>
              {onUnpublish && onPublish && (
                <StatusIconButton
                  status={attachment.status}
                  tooltip={attachment.status ? t('publish') : t('unpublish')}
                  onClick={() => {
                    if (attachment.status) {
                      onUnpublish([attachment.filename]);
                    } else {
                      onPublish([attachment.filename]);
                    }
                  }}
                />
              )}
              <DeleteIconButton
                tooltip={t('delete')}
                onClick={() => setIsOpenDeleteDialog(true)}
              />
            </ListItemSecondaryAction>
          </ListItem>
        </>
      )}
      <Divider component="li" />
    </>
  );
}
